import React, { createContext, useContext, useState, useMemo, ReactNode, useEffect } from 'react'
import { PaletteMode, useMediaQuery } from '@mui/material'
import { lightTheme, darkTheme } from './index'

export type LangKey = 'he' | 'en'
export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextType {
  mode: PaletteMode
  themeMode: ThemeMode
  setThemeMode: (mode: ThemeMode) => void
  toggleTheme: () => void
  theme: typeof lightTheme
  langKey: LangKey
  toggleLanguage: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
}

export const AppThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get initial theme preference from localStorage or default to system
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const savedPref = localStorage.getItem('theme-preference')
    if (savedPref === 'system' || savedPref === 'light' || savedPref === 'dark') {
      return savedPref as ThemeMode
    }
    // Fallback to older theme-mode key
    const savedMode = localStorage.getItem('theme-mode')
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode as ThemeMode
    }
    return 'system'
  })

  // Get initial language from localStorage or default to 'he'
  const [langKey, setLangKey] = useState<LangKey>(() => {
    const savedLang = localStorage.getItem('lang-key')
    return (savedLang as LangKey) || 'he'
  })

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('theme-preference', themeMode)
    // Keep old key in sync for any components bypassing context (if any)
    if (themeMode !== 'system') {
      localStorage.setItem('theme-mode', themeMode)
    }
  }, [themeMode])

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('lang-key', langKey)
  }, [langKey])

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  const mode: PaletteMode = useMemo(() => {
    if (themeMode === 'system') {
      return prefersDarkMode ? 'dark' : 'light'
    }
    return themeMode
  }, [themeMode, prefersDarkMode])

  const theme = useMemo(() => {
    return mode === 'light' ? lightTheme : darkTheme
  }, [mode])

  useEffect(() => {
    document.documentElement.style.colorScheme = mode
  }, [mode])

  const toggleTheme = () => {
    setThemeMode((prevMode) => {
      if (prevMode === 'system') return prefersDarkMode ? 'light' : 'dark'
      return prevMode === 'light' ? 'dark' : 'light'
    })
  }

  const toggleLanguage = () => {
    setLangKey((prevKey) => (prevKey === 'he' ? 'en' : 'he'))
  }

  const value = useMemo(() => ({
    mode,
    themeMode,
    setThemeMode,
    toggleTheme,
    theme,
    langKey,
    toggleLanguage,
  }), [mode, themeMode, theme, langKey])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

// Custom hook to use theme
export const useAppTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useAppTheme must be used within an AppThemeProvider')
  }
  return context
}
